import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Download,
  Send,
  X,
  Trash2,
  FileText,
  Building2,
  User,
  Car as CarIcon,
  Calendar,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/lib/api";

const PO_STATUSES = ["draft", "sent", "confirmed", "delivered", "cancelled"];

const STATUS_COLORS = {
  draft: "bg-white/5 text-white/60 border-white/15",
  sent: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30",
  confirmed: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtAud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n || 0);

const emptyPO = {
  application_id: null,
  dealer_name: "",
  dealer_abn: "",
  dealer_contact_name: "",
  dealer_email: "",
  dealer_phone: "",
  dealer_address: "",
  lessee_name: "",
  lessee_email: "",
  lessee_phone: "",
  lessee_address: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: new Date().getFullYear(),
  vehicle_variant: "",
  vehicle_vin: "",
  vehicle_colour: "",
  vehicle_price: "",
  registered_owner: "buyer",
  delivery_date: "",
  delivery_location: "",
  payment_terms: "Payment on delivery via bank transfer (EFT).",
  notes: "",
};

export default function PurchaseOrders({ prefill, onClearPrefill }) {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [initialForm, setInitialForm] = useState(emptyPO);
  const [selectedPO, setSelectedPO] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/purchase-orders", { params });
      setPos(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to load POs."));
    } finally { setLoading(false); }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Handle prefill (from application drawer)
  useEffect(() => {
    if (prefill) {
      setInitialForm({ ...emptyPO, ...prefill });
      setFormOpen(true);
      if (onClearPrefill) onClearPrefill();
    }
  }, [prefill, onClearPrefill]);

  const onCreated = (po) => {
    setPos((prev) => [po, ...prev]);
    setFormOpen(false);
    setInitialForm(emptyPO);
    toast.success(`${po.po_number} created`);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/purchase-orders/${id}`, { status: newStatus });
      setPos((prev) => prev.map((p) => (p.id === id ? data : p)));
      if (selectedPO?.id === id) setSelectedPO(data);
      toast.success(`Status → ${newStatus}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Update failed."));
    }
  };

  const remove = async (id, poNumber) => {
    if (!window.confirm(`Delete ${poNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/purchase-orders/${id}`);
      setPos((prev) => prev.filter((p) => p.id !== id));
      if (selectedPO?.id === id) setSelectedPO(null);
      toast.success("Purchase order deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Delete failed."));
    }
  };

  const download = async (po) => {
    try {
      const token = localStorage.getItem("vf_admin_token");
      const res = await fetch(`${API_BASE}/admin/purchase-orders/${po.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const send = async (po) => {
    if (!window.confirm(`Email PO ${po.po_number} to ${po.dealer_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/purchase-orders/${po.id}/send`);
      setPos((prev) => prev.map((p) => (p.id === po.id ? data.purchase_order : p)));
      if (selectedPO?.id === po.id) setSelectedPO(data.purchase_order);
      toast.success(`Sent to ${po.dealer_email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Email send failed."));
    }
  };

  return (
    <div data-testid="pos-section">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="pos-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search PO#, dealer, lessee, vehicle…"
            className="w-full bg-[#0a0a0a] border border-white/10 text-white pl-10 pr-4 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
          />
        </div>
        <select
          data-testid="pos-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#0a0a0a] border border-white/10 text-white px-4 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none capitalize"
        >
          <option value="all">All statuses</option>
          {PO_STATUSES.map((s) => (
            <option key={s} value={s} className="capitalize">{s}</option>
          ))}
        </select>
        <button
          data-testid="pos-new-btn"
          onClick={() => { setInitialForm(emptyPO); setFormOpen(true); }}
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#F6ECCC] transition-colors"
        >
          <Plus size={14} /> New PO
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#0a0a0a] border border-white/10">
        {loading ? (
          <div className="p-16 text-center text-white/50 text-sm">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-16 text-center text-white/40 text-sm" data-testid="pos-empty">
            No purchase orders yet. Click <span className="text-[#D4AF37]">&ldquo;New PO&rdquo;</span> to create your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#080808] border-b border-white/10">
                <tr className="text-left text-white/50 text-[10px] uppercase tracking-[0.18em]">
                  <th className="px-5 py-4">PO Number</th>
                  <th className="px-5 py-4 hidden md:table-cell">Dealer</th>
                  <th className="px-5 py-4 hidden lg:table-cell">Vehicle</th>
                  <th className="px-5 py-4">Price</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 hidden lg:table-cell">Created</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pos.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`po-row-${p.po_number}`}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="text-white font-mono text-xs">{p.po_number}</div>
                      <div className="text-xs text-white/40 md:hidden">{p.dealer_name}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-white/70">{p.dealer_name}</td>
                    <td className="px-5 py-4 hidden lg:table-cell text-white/70">
                      {p.vehicle_year} {p.vehicle_make} {p.vehicle_model}
                    </td>
                    <td className="px-5 py-4 text-[#D4AF37]">{fmtAud(p.vehicle_price)}</td>
                    <td className="px-5 py-4">
                      <StatusBadge status={p.status || "draft"} />
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-white/50 text-xs">{fmtDate(p.created_at)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 justify-end">
                        <IconBtn onClick={() => download(p)} title="Download PDF" testId={`po-download-${p.po_number}`}>
                          <Download size={14} />
                        </IconBtn>
                        <IconBtn onClick={() => send(p)} title="Email to dealer" testId={`po-send-${p.po_number}`}>
                          <Send size={14} />
                        </IconBtn>
                        <button
                          onClick={() => setSelectedPO(p)}
                          data-testid={`po-view-${p.po_number}`}
                          className="text-xs text-[#D4AF37] hover:text-[#F6ECCC] px-2"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create form modal */}
      {formOpen && (
        <PurchaseOrderForm
          initial={initialForm}
          onCancel={() => { setFormOpen(false); setInitialForm(emptyPO); }}
          onCreated={onCreated}
        />
      )}

      {/* Detail drawer */}
      {selectedPO && (
        <PODetailDrawer
          po={selectedPO}
          onClose={() => setSelectedPO(null)}
          onStatus={updateStatus}
          onDelete={remove}
          onDownload={download}
          onSend={send}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
        STATUS_COLORS[status] || STATUS_COLORS.draft
      }`}
    >
      {status}
    </span>
  );
}

function IconBtn({ children, onClick, title, testId }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="w-8 h-8 flex items-center justify-center border border-white/10 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
    >
      {children}
    </button>
  );
}

/* ---------------- Create Form Modal ---------------- */

function PurchaseOrderForm({ initial, onCancel, onCreated }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["dealer_name", "dealer_contact_name", "dealer_email",
                      "lessee_name", "vehicle_make", "vehicle_model", "vehicle_year", "vehicle_price"];
    const missing = required.filter((k) => !String(form[k] ?? "").trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_year: Number(form.vehicle_year),
        vehicle_price: Number(form.vehicle_price),
      };
      const { data } = await api.post("/admin/purchase-orders", payload);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed to create PO."));
    } finally { setSubmitting(false); }
  };

  return (
    <div
      data-testid="po-form-modal"
      className="fixed inset-0 z-50 flex justify-center items-start overflow-y-auto p-4 md:p-8"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative w-full max-w-3xl bg-[#0a0a0a] border border-white/10 my-8"
      >
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-5 flex items-start justify-between gap-4 z-10">
          <div>
            <div className="label-eyebrow mb-1">Purchase Order</div>
            <h2 className="font-display text-2xl text-white" style={{ fontWeight: 400 }}>Create new PO</h2>
          </div>
          <button type="button" onClick={onCancel} data-testid="po-form-close" className="text-white/60 hover:text-white p-1">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <Section title="Dealership (Seller)" icon={<Building2 size={14} />}>
            <Row>
              <Field label="Dealership Name *">
                <input data-testid="po-dealer-name" required value={form.dealer_name} onChange={update("dealer_name")} className={inputCls} />
              </Field>
              <Field label="ABN">
                <input data-testid="po-dealer-abn" value={form.dealer_abn} onChange={update("dealer_abn")} className={inputCls} />
              </Field>
            </Row>
            <Row>
              <Field label="Contact Name *">
                <input data-testid="po-dealer-contact" required value={form.dealer_contact_name} onChange={update("dealer_contact_name")} className={inputCls} />
              </Field>
              <Field label="Contact Email *">
                <input data-testid="po-dealer-email" type="email" required value={form.dealer_email} onChange={update("dealer_email")} className={inputCls} />
              </Field>
            </Row>
            <Row>
              <Field label="Phone">
                <input data-testid="po-dealer-phone" value={form.dealer_phone} onChange={update("dealer_phone")} className={inputCls} />
              </Field>
              <Field label="Address">
                <input data-testid="po-dealer-address" value={form.dealer_address} onChange={update("dealer_address")} className={inputCls} />
              </Field>
            </Row>
          </Section>

          <Section title="Lessee (End User)" icon={<User size={14} />}>
            <Row>
              <Field label="Full Name *">
                <input data-testid="po-lessee-name" required value={form.lessee_name} onChange={update("lessee_name")} className={inputCls} />
              </Field>
              <Field label="Email">
                <input data-testid="po-lessee-email" type="email" value={form.lessee_email} onChange={update("lessee_email")} className={inputCls} />
              </Field>
            </Row>
            <Row>
              <Field label="Phone">
                <input data-testid="po-lessee-phone" value={form.lessee_phone} onChange={update("lessee_phone")} className={inputCls} />
              </Field>
              <Field label="Address (for registration)">
                <input data-testid="po-lessee-address" value={form.lessee_address} onChange={update("lessee_address")} className={inputCls} placeholder="Street, Suburb, State Postcode" />
              </Field>
            </Row>
          </Section>

          <Section title="Vehicle" icon={<CarIcon size={14} />}>
            <Row>
              <Field label="Make *">
                <input data-testid="po-vehicle-make" required value={form.vehicle_make} onChange={update("vehicle_make")} className={inputCls} />
              </Field>
              <Field label="Model *">
                <input data-testid="po-vehicle-model" required value={form.vehicle_model} onChange={update("vehicle_model")} className={inputCls} />
              </Field>
            </Row>
            <Row>
              <Field label="Year *">
                <input data-testid="po-vehicle-year" type="number" min="1980" max="2030" required value={form.vehicle_year} onChange={update("vehicle_year")} className={inputCls} />
              </Field>
              <Field label="Variant / Trim">
                <input data-testid="po-vehicle-variant" value={form.vehicle_variant} onChange={update("vehicle_variant")} className={inputCls} />
              </Field>
              <Field label="Colour">
                <input data-testid="po-vehicle-colour" value={form.vehicle_colour} onChange={update("vehicle_colour")} className={inputCls} />
              </Field>
            </Row>
            <Row>
              <Field label="VIN">
                <input data-testid="po-vehicle-vin" value={form.vehicle_vin} onChange={update("vehicle_vin")} className={inputCls} />
              </Field>
              <Field label="Drive-Away Price (AUD) *">
                <input data-testid="po-vehicle-price" type="number" min="0" step="1" required value={form.vehicle_price} onChange={update("vehicle_price")} className={inputCls} />
              </Field>
            </Row>

            {/* Registered Owner toggle */}
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 block">
                Registered Owner
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <OwnerOption
                  testId="po-reg-owner-buyer"
                  active={form.registered_owner === "buyer"}
                  onClick={() => setForm((f) => ({ ...f, registered_owner: "buyer" }))}
                  title="Keys To Own"
                  subtitle="Standard — leasing company holds registration."
                />
                <OwnerOption
                  testId="po-reg-owner-lessee"
                  active={form.registered_owner === "lessee"}
                  onClick={() => setForm((f) => ({ ...f, registered_owner: "lessee" }))}
                  title="Register to Lessee"
                  subtitle="Vehicle registered in lessee&apos;s name (with ownership caveat)."
                />
              </div>
            </div>
          </Section>

          <Section title="Delivery & Payment" icon={<Calendar size={14} />}>
            <Row>
              <Field label="Delivery Date">
                <input
                  data-testid="po-delivery-date"
                  type="text"
                  inputMode="numeric"
                  placeholder="DD/MM/YYYY  (or 'TBC')"
                  value={form.delivery_date}
                  onChange={update("delivery_date")}
                  className={inputCls}
                />
              </Field>
              <Field label="Delivery Location">
                <input data-testid="po-delivery-location" value={form.delivery_location} onChange={update("delivery_location")} className={inputCls} />
              </Field>
            </Row>
            <Field label="Payment Terms">
              <input data-testid="po-payment-terms" value={form.payment_terms} onChange={update("payment_terms")} className={inputCls} />
            </Field>
            <Field label="Notes">
              <textarea data-testid="po-notes" rows="3" value={form.notes} onChange={update("notes")} className={`${inputCls} resize-none`} />
            </Field>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-white/10 px-6 py-4 flex items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className="text-sm text-white/60 hover:text-white px-4 py-2">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="po-form-submit"
            disabled={submitting}
            className="bg-[#D4AF37] text-black px-6 py-2.5 text-sm font-medium hover:bg-[#F6ECCC] transition-colors disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create Purchase Order"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors";

function Field({ label, children }) {
  return (
    <label className="block flex-1">
      <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Row({ children }) {
  return <div className="flex flex-col md:flex-row gap-4">{children}</div>;
}

function Section({ title, icon, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-white/10">
        <div className="text-[#D4AF37]">{icon}</div>
        <div className="label-eyebrow">{title}</div>
      </div>
      {children}
    </div>
  );
}

function OwnerOption({ active, onClick, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 border transition-all ${
        active
          ? "border-[#D4AF37] bg-[#D4AF37]/10"
          : "border-white/10 hover:border-white/25 bg-transparent"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            active ? "border-[#D4AF37]" : "border-white/30"
          }`}
        >
          {active && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
        </span>
        <div>
          <div className={`text-sm ${active ? "text-white" : "text-white/80"}`}>{title}</div>
          <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

/* ---------------- Detail Drawer ---------------- */

function PODetailDrawer({ po, onClose, onStatus, onDelete, onDownload, onSend }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="po-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-xl h-full bg-[#0a0a0a] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-5 flex items-start justify-between gap-4 z-10">
          <div>
            <div className="label-eyebrow mb-1">Purchase Order</div>
            <h2 className="font-display text-2xl text-white font-mono" style={{ fontWeight: 400 }}>{po.po_number}</h2>
          </div>
          <button onClick={onClose} data-testid="po-drawer-close" className="text-white/60 hover:text-white p-1">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Status control */}
          <div className="flex items-center justify-between border border-white/10 bg-[#050505] p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40">Status</div>
            <select
              data-testid="po-drawer-status"
              value={po.status || "draft"}
              onChange={(e) => onStatus(po.id, e.target.value)}
              className="bg-[#0a0a0a] border border-white/15 text-white text-sm px-3 py-1.5 focus:border-[#D4AF37] focus:outline-none capitalize"
            >
              {PO_STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onDownload(po)}
              data-testid="po-drawer-download"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-4 py-3 text-sm font-medium hover:bg-[#F6ECCC] transition-colors"
            >
              <Download size={14} /> Download PDF
            </button>
            <button
              onClick={() => onSend(po)}
              data-testid="po-drawer-send"
              className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors"
            >
              <Send size={14} /> Email Dealer
            </button>
          </div>
          {po.sent_at && (
            <p className="text-xs text-emerald-300/70">✓ Emailed on {fmtDate(po.sent_at)}</p>
          )}

          {/* Sections */}
          <DetailSection title="Dealership" icon={<Building2 size={14} />}>
            <D k="Name" v={po.dealer_name} />
            <D k="ABN" v={po.dealer_abn} />
            <D k="Contact" v={po.dealer_contact_name} />
            <D k="Email" v={po.dealer_email} />
            <D k="Phone" v={po.dealer_phone} />
            <D k="Address" v={po.dealer_address} />
          </DetailSection>

          <DetailSection title="Lessee" icon={<User size={14} />}>
            <D k="Name" v={po.lessee_name} />
            <D k="Email" v={po.lessee_email} />
            <D k="Phone" v={po.lessee_phone} />
            <D k="Address" v={po.lessee_address} />
          </DetailSection>

          <DetailSection title="Vehicle" icon={<CarIcon size={14} />}>
            <D k="Make" v={po.vehicle_make} />
            <D k="Model" v={po.vehicle_model} />
            <D k="Year" v={po.vehicle_year} />
            <D k="Variant" v={po.vehicle_variant} />
            <D k="VIN" v={po.vehicle_vin} />
            <D k="Colour" v={po.vehicle_colour} />
            <D
              k="Registered To"
              v={po.registered_owner === "lessee"
                  ? <span className="text-[#D4AF37]">{po.lessee_name || "Lessee"} <span className="text-white/50 text-xs">(with ownership caveat)</span></span>
                  : <span>Keys To Own <span className="text-white/50 text-xs">(standard)</span></span>}
            />
            <D k="Drive-Away Price" v={<span className="text-[#D4AF37]">{fmtAud(po.vehicle_price)}</span>} />
          </DetailSection>

          <DetailSection title="Delivery & Payment" icon={<DollarSign size={14} />}>
            <D k="Delivery Date" v={po.delivery_date} />
            <D k="Location" v={po.delivery_location} />
            <D k="Payment Terms" v={po.payment_terms} />
          </DetailSection>

          {po.notes && (
            <DetailSection title="Notes" icon={<FileText size={14} />}>
              <div className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">{po.notes}</div>
            </DetailSection>
          )}

          <D k="Created" v={fmtDate(po.created_at)} />

          <button
            onClick={() => onDelete(po.id, po.po_number)}
            data-testid="po-drawer-delete"
            className="w-full inline-flex items-center justify-center gap-2 border border-red-500/40 text-red-300 px-4 py-2.5 text-sm hover:bg-red-500/10 transition-colors mt-4"
          >
            <Trash2 size={14} /> Delete Purchase Order
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 pb-2 border-b border-white/10 mb-3">
        <div className="text-[#D4AF37]">{icon}</div>
        <div className="label-eyebrow">{title}</div>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function D({ k, v }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="w-32 text-white/40 text-xs uppercase tracking-[0.14em] pt-0.5 flex-shrink-0">{k}</div>
      <div className="text-white flex-1 break-words">{v || "—"}</div>
    </div>
  );
}
